/**
 * AIDLC product catalog — frontend registry.
 * Subscription gating reads tenant.settings.products (defaults keep PM enabled).
 */

export const PRODUCT_BRAND = 'CoTaskr';

export const AIDLC_NAME = 'AIDLC';
export const AIDLC_FULL_NAME = 'Automated Intelligent Delivery Lifecycle Control';
export const AIDLC_TAGLINE =
  'Automated delivery lifecycle — tools that act for your org.';

/** @deprecated Use AIDLC_NAME */
export const WORKSPACE_NAME = AIDLC_NAME;
/** @deprecated Use AIDLC_TAGLINE */
export const WORKSPACE_TAGLINE = AIDLC_TAGLINE;

export type ProductId = 'pm' | 'code' | 'ship' | 'lenc';

/** Legacy ids — map to current catalog entries (ledger/cost product removed from catalog). */
export const LEGACY_PRODUCT_IDS = ['ledger', 'watch', 'cost'] as const;

export function normalizeProductId(id: string): ProductId | null {
  if (id === 'watch') return 'lenc';
  if (id === 'pm' || id === 'code' || id === 'ship' || id === 'lenc') {
    return id;
  }
  return null;
}

export type ProductCatalogStatus = 'available' | 'coming_soon';

export type ProductIconKey = 'layout' | 'code' | 'package' | 'pulse';

export type ProductLaunchMode = 'internal' | 'external';

export type CostSourceProduct = 'pm' | 'code' | 'ship' | 'lenc' | 'external';

export interface ProductDefinition {
  id: ProductId;
  name: string;
  shortName: string;
  description: string;
  routePrefix: string;
  catalogStatus: ProductCatalogStatus;
  iconKey: ProductIconKey;
  launchMode: ProductLaunchMode;
  appUrl?: string;
  costEventTypes?: string[];
  pillClass: string;
  pillIconColor: string;
  tagClass: string;
  tagHoverClass: string;
  previewRingClass: string;
}

const DEFAULT_APP_URLS: Record<ProductId, string | undefined> = {
  pm: undefined,
  code: import.meta.env.VITE_COTASKR_CODE_URL || 'https://code.cotaskr.com',
  ship: import.meta.env.VITE_COTASKR_SHIP_URL || 'https://ship.cotaskr.com',
  lenc: import.meta.env.VITE_COTASKR_LENC_URL || import.meta.env.VITE_COTASKR_WATCH_URL || 'https://lenc.cotaskr.com',
};

export const PRODUCTS: ProductDefinition[] = [
  {
    id: 'pm',
    name: 'CoTaskr Plan',
    shortName: 'Plan',
    description: 'Project delivery, time tracking, and economics',
    routePrefix: '/',
    catalogStatus: 'available',
    iconKey: 'layout',
    launchMode: 'internal',
    costEventTypes: ['labor', 'ai_usage', 'economics_burn', 'budget_threshold'],
    pillClass: 'login-smart-pill--pm',
    pillIconColor: 'text-blue-700',
    tagClass: 'border-brand/45 bg-brand text-white',
    tagHoverClass: 'hover:bg-brand-dark',
    previewRingClass: 'ring-brand/30',
  },
  {
    id: 'code',
    name: 'CodeFx',
    shortName: 'CodeFx',
    description: 'Software risk intelligence — scan, prove, and fix',
    routePrefix: '/code',
    catalogStatus: 'available',
    iconKey: 'code',
    launchMode: 'internal',
    appUrl: DEFAULT_APP_URLS.code,
    costEventTypes: ['dev_license', 'ai_tokens', 'repo_storage', 'ci_minutes'],
    pillClass: 'login-smart-pill--code',
    pillIconColor: 'text-violet-700',
    tagClass: 'border-violet-500/50 bg-violet-600 text-white',
    tagHoverClass: 'hover:bg-violet-700',
    previewRingClass: 'ring-violet-300/60',
  },
  {
    id: 'ship',
    name: 'CoTaskr Ship',
    shortName: 'Ship',
    description: 'Release management, deployments, and go-live',
    routePrefix: '/ship',
    catalogStatus: 'coming_soon',
    iconKey: 'package',
    launchMode: 'external',
    appUrl: DEFAULT_APP_URLS.ship,
    costEventTypes: ['deploy_compute', 'cloud_infra', 'build_minutes', 'release_rollback'],
    pillClass: 'login-smart-pill--ship',
    pillIconColor: 'text-amber-700',
    tagClass: 'border-amber-500/50 bg-amber-600 text-white',
    tagHoverClass: 'hover:bg-amber-700',
    previewRingClass: 'ring-amber-300/60',
  },
  {
    id: 'lenc',
    name: 'CoTaskr Lenc',
    shortName: 'Lenc',
    description: 'Monitoring, alerts, and operational visibility',
    routePrefix: '/lenc',
    catalogStatus: 'coming_soon',
    iconKey: 'pulse',
    launchMode: 'external',
    appUrl: DEFAULT_APP_URLS.lenc,
    costEventTypes: ['monitoring_service', 'alert_channel', 'incident_duration'],
    pillClass: 'login-smart-pill--lenc',
    pillIconColor: 'text-sky-700',
    tagClass: 'border-sky-500/50 bg-sky-600 text-white',
    tagHoverClass: 'hover:bg-sky-700',
    previewRingClass: 'ring-sky-300/60',
  },
];

export const PRODUCT_BY_ID = Object.fromEntries(
  PRODUCTS.map((product) => [product.id, product])
) as Record<ProductId, ProductDefinition>;

export const PRODUCT_IDS = PRODUCTS.map((product) => product.id);

import {
  WORKSPACE_BILLABLE_PRODUCT_IDS,
  estimateWorkspaceMonthlyUsd,
  type WorkspaceBillableProductId,
} from '@/config/pricing';

export { WORKSPACE_BILLABLE_PRODUCT_IDS, estimateWorkspaceMonthlyUsd };
export type { WorkspaceBillableProductId };

export const PRODUCT_PRICING: Record<
  ProductId,
  { label: string; priceUsd: number; unit: string; competitorNote: string; billable?: boolean }
> = {
  pm: { label: 'Plan', priceUsd: 10, unit: '/user/mo', competitorNote: 'vs Linear $8–14, Jira $7.75–16', billable: true },
  code: { label: 'CodeFx', priceUsd: 12, unit: '/dev/mo', competitorNote: 'vs Copilot Business $19', billable: true },
  ship: { label: 'Ship', priceUsd: 15, unit: '/seat/mo', competitorNote: 'vs Render $25+, Vercel $20', billable: true },
  lenc: { label: 'Lenc', priceUsd: 29, unit: '/org/mo', competitorNote: 'vs Sentry ~$26 team', billable: true },
};

export const WORKSPACE_BILLABLE_PRODUCTS = PRODUCTS.filter((p) =>
  WORKSPACE_BILLABLE_PRODUCT_IDS.includes(p.id as WorkspaceBillableProductId),
);

export const AIDLC_SUITE_BUNDLES = [
  { id: 'delivery', name: 'Workspace Delivery', products: ['pm'] as ProductId[], priceHint: '$10/user flat' },
  { id: 'dev', name: 'Workspace Dev', products: ['pm', 'code'] as ProductId[], priceHint: '$22/user flat' },
  {
    id: 'release',
    name: 'Workspace Release',
    products: ['pm', 'code', 'ship'] as ProductId[],
    priceHint: '$37/user flat',
  },
  {
    id: 'full',
    name: 'Workspace Full',
    products: ['pm', 'code', 'ship', 'lenc'] as ProductId[],
    priceHint: '$66/org + seats (flat list)',
  },
] as const;
